import { Box, Button, Tabs, Tab, Tooltip } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";

interface LeadsStatusTabsProps {
  data: LeadsManagementData;
}

const STATUS_TAB_TRACKER_IDS: Record<string, string> = {
  Success: "1030",
  Failure: "1031",
  InternalDuplication: "1032",
  BrandDuplication: "1033",
  Rejected: "979",
};

export default function LeadsStatusTabs({ data }: LeadsStatusTabsProps) {
  const {
    status,
    statusTabs,
    setStatus,
    isLoading,
    selectedCount,
    requestBulkReport,
    canReportLeads,
    requestBulkReject,
    canRejectLeads,
    allSelectedRejectable,
    allSelectedReportable,
  } = data;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mt: 2,
      }}
    >
      <Tabs
        value={status}
        onChange={(_, value) => setStatus(value)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          display: "inline-flex",
          maxWidth: "100%",
          backgroundColor: "grey.100",
          borderRadius: "8px",
          minHeight: 0,
          p: 0.5,
                    "& .MuiTab-root": {
            minHeight: 0,
            minWidth: "auto",
            borderRadius: "4px",
            px: 2,
            py: 0.5,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: "22px",
            letterSpacing: "0.46px",
            color: "text.disabled",
            "&:hover": { color: "text.primary" },
          },
          "& .Mui-selected": {
            color: (theme) => `${theme.palette.text.primary} !important`,
            fontWeight: 500,
            backgroundColor: "common.white",
          },
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        <Tab value="All" label="ALL" disabled={isLoading} data-tracker="1027" />
        {statusTabs.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            label={tab.label.toUpperCase()}
            disabled={isLoading}
            data-tracker={STATUS_TAB_TRACKER_IDS[tab.key]}
          />
        ))}
      </Tabs>

      {/* Moved here from the floating LeadsBulkActionBar — same
          reject/report actions, same enablement rules (selectedCount>0 plus
          the same canReportLeads/canRejectLeads permission + eligibility
          checks), just anchored next to the status tabs instead of a
          bottom-fixed bar. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        <Tooltip
          title={
            selectedCount > 0 && !allSelectedRejectable
              ? "Leads that are already reported or rejected can't be rejected"
              : ""
          }
          disableHoverListener={selectedCount === 0 || allSelectedRejectable}
        >
          <span>
            <Button
              size="small"
              disabled={selectedCount === 0 || !canRejectLeads}
              onClick={requestBulkReject}
              data-tracker="980"
              sx={{ color: "secondary.main", "&:hover": { bgcolor: "rgba(156, 39, 176, 0.04)" } }}
            >
              Reject
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            selectedCount > 0 && !allSelectedReportable
              ? "Only Pending and Duplicate IP leads can be reported to the brand"
              : ""
          }
          disableHoverListener={selectedCount === 0 || allSelectedReportable}
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              disabled={!allSelectedReportable || !canReportLeads}
              onClick={requestBulkReport}
              data-tracker="930"
            >
              Report to brand
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
